import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, retry, map, tap } from 'rxjs/operators';
import { BaseStorageService } from '../storage/base-storage.service';
import { SessionType, SessionResponse, Session } from '../../modal/session.modal';
import { environment } from '../../../environments/environment';

// Authentication interfaces
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
    current_password: string;
    new_password: string;
    new_password_confirmation: string;
}

export interface ResetPasswordRequest {
  email: string;
  verification_code: string;
  password: string;
  password_confirmation: string;
}

export interface SendPasswordResetCodeResponse {
  status: ResponseStatus;
  message: string;
}

export interface ResetPasswordResponse {
  status: ResponseStatus;
  message: string;
}

export interface ChangePasswordResponse {
  success: boolean;
  message: string;
}

export enum ResponseStatus {
  OK = 'ok',
  FAIL = 'fail'
}

export enum LeaveRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected'
}

export enum LeaveRequestType {
  casual = 'casual',
  sick = 'sick'
}

export interface AddLeaveRequestRequest {
  session_id: string;
  type: LeaveRequestType;
}

export interface AddLeaveRequestResponse {
  status: ResponseStatus;
  message: string;
}

export interface LeaveRequest {
  id: string;
  type: LeaveRequestType;
  session_type: SessionType;
  session_weekday: number;
  session_date: string;
  session_start_time: string;
  session_end_time: string;
  session_venue_name: string;
  duration: number;
  status: LeaveRequestStatus;
  created_at: string
}

export interface AlertPopup {
  id: string;
  title_en: string;
  title_zh_hk: string;
  title_zh_cn: string;
  content_en: string;
  content_zh_hk: string;  
  content_zh_cn: string;
}

export interface AlertPopupResponse {
  status: ResponseStatus;
  message: string;
  alert: AlertPopup;
}

export interface LeaveRequestResponse {
  status: ResponseStatus;
  message: string;
  leave_requests: LeaveRequest[];
}

export interface Rider {
  id: string;
  email: string;
  school_id: string;
  email_verified_at: string;
  photo: string;
  chi_name: string;
  eng_name: string;
  other_name: string;
  birth: string;
  nationality: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  head_size: string;
  diagnosis: string;
  parent_name: string;
  parent_relationship: string;
  parent_mobile: string;
  parent_email: string;
  parent_home: string;
  parent_address: string;
  emergency_person: string;
  emergency_mobile: string;
  other_therapy: string;
  medication: string;
  school_name: string;
  school_address: string;
  school_contact_person: string;
  school_tel: string;
  school_fax: string;
  school_email: string;
  riding_experience: string;
  assessor_name: string;
  assessor_profession: string;
  assessor_tel: string;
  assessor_email: string;
  assessment_date: string;
  assessor_signature: string;
  asd: string;
  asd_remark: string;
  adhd: string;
  adhd_remark: string;
  sld: string;
  sld_remark: string;
  ind: string;
  ind_remark: string;
  comd: string;
  comd_remark: string;
  visi: string;
  visi_remark: string;
  heari: string;
  heari_remark: string;
  bed: string;
  bed_remark: string;
  bed_specify: string;
  phyd: string;
  phyd_remark: string;
  others: string;
  others_remark: string;
  ambulatory: string;
  ambulatory_other: string;
  non_ambulatory: string;
  non_ambulatory_other: string;
  part_body_affected: string;
  prosthesis: string;
  head_control: string;
  trunk_control: string;
  data_use: boolean;
  tnc: boolean;
  photo_use_rider: boolean;
  rda_policy: boolean;
  statement_rider_medical_condition: string;
  medical_certificate_down_syndrome: string;
  medical_certificate_heart_disease: string;
  statement_release_indemnity: string;
  created_at: string;
  updated_at: string;
  name_en: string;
  name_zh_hk: string;
  walk: boolean;
}

export interface GetProfileResponse {
  status: ResponseStatus;
  message: string;
  rider: Rider;
}

export interface LoginResponse {
  token: string;
  rider: Rider;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
  };

  constructor(
    private http: HttpClient,
    private storageService: BaseStorageService
  ) {
    // Initialize token if present
    // const token = this.storageService.get('auth_token');
    // if (token) {
    //   this.setAuthToken(token);
    // }
  }

  // Handle API errors
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('API error:', error);
    return throwError(() => error);
  }

  // HTTP methods
  private get<T>(endpoint: string, params?: any): Observable<T> {
    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, {
      ...this.httpOptions,
      params
    }).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  private post<T>(endpoint: string, body: any): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, body, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  private put<T>(endpoint: string, body: any): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, body, this.httpOptions).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  private patch<T>(endpoint: string, body: any): Observable<T> {
    return this.http.patch<T>(`${this.apiUrl}/${endpoint}`, body, this.httpOptions).pipe(
      catchError(this.handleError)
    );
  }

  private delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, this.httpOptions).pipe(
      catchError(this.handleError)
    );
  }

  // Authentication methods
  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.post<LoginResponse>('rider/login', credentials).pipe(
      tap((response) => {
        console.log("login response", response);
        this.storageService.set('auth_token', response.token);
        this.storageService.set('user', JSON.stringify(response.rider));
        this.storageService.set('email', response.rider.email);
        this.setAuthToken(response.token); 
      }),
      catchError((error) => {
        this.storageService.set('auth_token', '');
        this.storageService.set('user', '');
        this.storageService.set('email', '');
        throw error;
      })
    );
  }

  changePassword(request: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    return this.post<ChangePasswordResponse>('rider/changePassword', request);
  }

  sendPasswordResetCode(request: { email: string }): Observable<SendPasswordResetCodeResponse> {
    return this.post<SendPasswordResetCodeResponse>('rider/sendPasswordResetCode', request).pipe(
      map(response => {
        if (response.status !== ResponseStatus.OK) {
          throw new Error(response.message);
        }
        return response;
      })
    );
  }

  resetPassword(request: ResetPasswordRequest): Observable<ResetPasswordResponse> {
    return this.post<ResetPasswordResponse>('rider/resetPassword', request).pipe(
      map(response => {
        if (response.status !== ResponseStatus.OK) {
          throw new Error(response.message);
        }
        return response;
      })
    );
  }

  logout(): Observable<void> {
    return this.post<void>('rider/logout', {}).pipe(
      tap(
        () => {
          console.log('Logout successful');
          // Clear storage after successful logout
          this.storageService.set('auth_token', '');
          this.storageService.set('user', '');
          this.storageService.set('email', '');
          this.clearAuthToken();
        },
        (error) => {
          console.error('Logout failed:', error);
          // Still clear storage even if API call fails
          this.storageService.set('auth_token', '');
          this.storageService.set('user', '');
          this.storageService.set('email', '');
          this.clearAuthToken();
        }
      )
    );
  }

  getSessions(): Observable<Session[]> {
    return this.get<SessionResponse>('rider/sessions').pipe(
      map(response => {
        if (response.status !== ResponseStatus.OK) {
          throw new Error(response.message);
        }
        return response.sessions;
      })
    );
  } 

  getLeaveRequests(): Observable<LeaveRequest[]> {
    return this.get<LeaveRequestResponse>('rider/getLeaveRequests').pipe(
      map(response => {
        if (response.status !== ResponseStatus.OK) {
          throw new Error(response.message);
        }
        return response.leave_requests;
      })
    );
  }

    // Leave request methods
    addLeaveRequest(request: AddLeaveRequestRequest): Observable<AddLeaveRequestResponse> {
      return this.post<AddLeaveRequestResponse>('rider/addLeaveRequest', request).pipe(
        map(response => {
          if (response.status !== ResponseStatus.OK) {
            throw new Error(response.message);
          }
          return response;
        })
      );
    }


    
  getUser(): Observable<any> {
    const userStr = this.storageService.get('user');
    if (userStr) {
      return of(JSON.parse(userStr));
    }
    return of(null);
  }

  getProfile(): Observable<Rider> {
    return this.get<GetProfileResponse>('rider/getProfile').pipe(
      map(response => {
        if (response.status !== ResponseStatus.OK) {
          throw new Error(response.message);
        }
        return response.rider;
      })
    );
  }

  getAlertPopup(): Observable<AlertPopup> {
    return this.get<AlertPopupResponse>('rider/alert').pipe(
      map(response => {
        if (response.status !== ResponseStatus.OK) {
          throw new Error(response.message);
        }
        return response.alert;
      })
    );
  }

  getToken(): string | null {
    return this.storageService.get('auth_token');
  }

  isAuthenticated(): boolean {
    const token = this.storageService.get('auth_token');
    console.log("isAuthenticated", { tokenExists: !!token });
    console.log("Token: ", token);
    return !!token;
  }

  // Token management
  setAuthToken(token: string): void {
    this.storageService.set('auth_token', token);
    // this.httpOptions.headers = this.httpOptions.headers.set('Authorization', `Bearer ${token}`);
  }

  clearAuthToken(): void {
    localStorage.removeItem('auth_token');
    this.httpOptions.headers = this.httpOptions.headers.delete('Authorization');
  }


}
